import type {
    MouseEvent,
    SyntheticEvent,
    HTMLAttributes,
    CSSProperties
} from 'react';
import { defaultStyle } from './image.css';

const placeHolderImg =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 3 2'%3E%3C/svg%3E";

const generateSrcSet = (img: Img[]) => {
    let res = '';

    img.forEach((item, idx) => {
        // add srcset per width
        res += `${item.src} ${item.width}w`;

        // add comma at the end if not the last item
        if (idx < img.length - 1) {
            res += ', ';
        }
    });

    return res;
};

export type StaticGoogleMapImgStyles = Pick<
    CSSProperties,
    | 'objectFit'
    | 'objectPosition'
    | 'height'
    | 'width'
    | 'borderRadius'
    | 'display'
>;

type Img = {
    width: number;
    src: string;
};

export type ImageProps = {
    src: string;
    alt: string;
    lazy?: boolean;
    important?: boolean;
    width?: number;
    height?: number;
    onClick?: (e: MouseEvent<HTMLImageElement>) => void;
    title?: string;
    onLoad?: (e: SyntheticEvent<HTMLImageElement, Event>) => void;
    onError?: (e: SyntheticEvent<HTMLImageElement, Event>) => void;
    decorative?: boolean;
    style?: StaticGoogleMapImgStyles | string;
    srcSet?: string;
    sizes?: string;
    responsiveImgList?: Img[];
} & HTMLAttributes<HTMLImageElement>;

export function Image({
    decorative,
    lazy,
    important,
    responsiveImgList,
    srcSet,
    onError,
    className,
    ...props
}: ImageProps) {
    const loading = lazy ? 'lazy' : important ? 'eager' : undefined;

    function handleError(event: SyntheticEvent<HTMLImageElement, Event>) {
        event.currentTarget.src = placeHolderImg;
        onError?.(event);
    }

    return (
        <img
            {...props}
            className={`${defaultStyle} ${className}`}
            onError={handleError}
            loading={loading}
            alt={decorative ? '' : props.alt}
            srcSet={
                responsiveImgList ? generateSrcSet(responsiveImgList) : srcSet
            }
        />
    );
}

export default Image;
